<template>
    <div class="huddle-details">
        <b-navbar class="px-2 mb-3" toggleable="md" type="dark" variant="primary">
            <b-navbar-brand href="#"><span class="ml-4">Next 2 Weeks Net Scheduled Production</span></b-navbar-brand>
            <b-navbar-toggle class="ml-auto" target="nav_collapse3"></b-navbar-toggle>
            <b-collapse is-nav id="nav_collapse3">
            <!-- Right aligned nav items -->
            <b-navbar-nav class="ml-auto">
                <b-nav-item href="#">
                <button type="button" id="close-tooltip" class="btn mr-xs btn-default btn-sm" @click="closeSlideOutPanel"><i class="la la-remove"></i></button>
                <b-tooltip target="close-tooltip" triggers="hover">
                    Close
                </b-tooltip>
                </b-nav-item>                
            </b-navbar-nav>
            </b-collapse>
        </b-navbar>

        <PopupErrorDisplay/>

        <!-- Loading state -->
        <HuddleDetailsSkeleton v-if="loadingStatus" />

        <!-- No data message -->
        <b-container fluid v-if="!loadingStatus && (!detailData || Object.keys(detailData).length === 0)">
            <b-row>
                <b-col>
                    <div class="empty-state-box mt-4">
                        <div class="empty-icon">
                            <i class="fa fa-calendar-o"></i>
                        </div>
                        <h5 class="empty-title">No Scheduled Appointments</h5>
                        <p class="empty-text">There are no scheduled appointments for the next 2 weeks. Once appointments are scheduled, they will appear here with production details.</p>
                    </div>
                </b-col>
            </b-row>
        </b-container>

        <b-container fluid v-if="detailData && Object.keys(detailData).length > 0">
            <b-row>
                <b-col>
                    <b-alert v-if="!this.providerGoals"
                    show            
                    dismissible
                    fade
                    variant="warning"            
                    >
                        <h4 class="alert-heading">Provider Goals Missing!</h4>
                        <p>You have not set up your goals for each provider yet. Please do that to better utilize this page. To do so, please close this page and set the goals on the main Huddle page and then return to this page.</p>
                    </b-alert>
                </b-col>
            </b-row>            
            <!-- Monthly goal + suggested weekly pace reference -->
            <b-row v-if="monthlyGoal > 0" class="mb-2">
                <b-col cols="12">
                    <div class="huddle-monthly-ref">
                        <span class="hmr-label">Monthly Net Production Goal:</span>
                        <span class="hmr-value">{{ monthlyGoal | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue) }}</span>
                        <span class="hmr-sep">·</span>
                        <span class="hmr-pace">to pace, aim ~{{ suggestedWeeklyPace | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue) }}/week</span>
                    </div>
                </b-col>
            </b-row>

            <template v-for="(row, rowKey) in detailData">
                <!-- Weekly rollup banner (Scheduled vs fixed Weekly Goal) -->
                <b-row :key="`wk-${rowKey}`" class="week-summary-row">
                    <b-col cols="12" lg="6">
                        <div class="huddle-week-summary" :class="`is-${getWeeklyVariant(rowKey)}`">
                            <div class="hws-head">
                                <span class="hws-week">Week of {{ getWeekRangeLabel(rowKey) }}</span>
                                <span class="hws-figs">
                                    Scheduled <strong>{{ getWeeklyScheduled(rowKey) | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue) }}</strong>
                                    <template v-if="getWeeklyGoal(rowKey) > 0">
                                        <span class="hws-slash">/</span> Weekly Goal <strong>{{ getWeeklyGoal(rowKey) | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue) }}</strong>
                                        <span class="hws-pct">{{ getWeeklyGoalPercent(rowKey) }}%</span>
                                        <span v-if="weeklyGoalMet(rowKey)" class="hws-thumb"><i class="fa fa-thumbs-up" /></span>
                                    </template>
                                </span>
                            </div>
                            <b-progress v-if="getWeeklyGoal(rowKey) > 0" class="hws-bar bg-gray-lighter mt-1" :value="getWeeklyGoalPercent(rowKey)" :max="100" :variant="getWeeklyVariant(rowKey)" />
                            <div class="hws-foot" v-if="getWeeklyGoal(rowKey) > 0">
                                <span v-if="weeklyGoalMet(rowKey)">Weekly goal met — <strong>{{ Math.abs(getWeeklyDiff(rowKey)) | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue) }}</strong> above goal 🎉</span>
                                <span v-else>Needed to reach weekly goal: <strong>{{ getWeeklyDiff(rowKey) | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue) }}</strong></span>
                            </div>
                        </div>
                    </b-col>
                </b-row>

                <b-row :key="rowKey" class="mb-4">
               <b-col v-for="(col, colKey) in row" :key="colKey" md="12" sm="12" lg="3" xl="3" >
                    <Widget
                    class=""
                    :title="`<h4><span class='fw-semi-bold'>${getDayName(colKey)}</span> <span class='text-muted small'>(${formatDate(colKey, siteTimezone)})</span></h4>`"
                    customHeader :scheduledGoalMet="scheduledGoalMet(rowKey, colKey)"
                    >
                        <div v-for="(provider, providerKey) in col" :key="providerKey">
                            <div class="clearfix fs-mini mt-3" >
                                <span class="pull-right m-0 fw-semi-bold">Goal</span>
                                <span class="fw-semi-bold">{{getProviderName(provider.ProvNum)}} <span class="small"> ({{provider.Abbr}}) </span></span>
                            </div>
                            
                            <b-progress class="bg-gray-lighter progress-xs " :value="getProviderGoalPercent(provider.ScheduledProduction, getProviderGoal(provider.ProvNum, colKey))" :max="100" :variant="getVariant(provider.ScheduledProduction, getProviderGoal(provider.ProvNum, colKey))"/>

                            <div class="clearfix fs-mini" style="border-bottom: 1px solid #e7e7e7;padding-top:2px;">
                                <span class="pull-right m-0 fw-semi-bold mb-3">{{getProviderGoal(provider.ProvNum, colKey) | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</span>
                                <span class="fs-mini">Scheduled: <span class="fw-semi-bold">{{provider.ScheduledProduction | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</span></span>
                                <p class="fs-mini" v-if="getDiffBetweenGoalAndScheduled(provider.ScheduledProduction, getProviderGoal(provider.ProvNum, colKey)) > 0">Needed to reach Goal: <span class="fw-semi-bold">{{getDiffBetweenGoalAndScheduled(provider.ScheduledProduction, getProviderGoal(provider.ProvNum, colKey)) | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</span></p>
                            </div> 
                        </div>
                        <div style="background-color:#f9fbfd;padding-left:10px;padding-right:10px;padding-top:5px;">  
                            <div class="clearfix fs-mini mt-3">
                                    <span class="pull-right m-0 fw-semi-bold">GOAL</span>
                                    <b-button class="btn-sm huddle-details-highlight">
                                        <span class="fw-bold" >TOTAL: {{getTotalScheduledForDay(rowKey, colKey) | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</span>
                                    </b-button>
                                    <!--<span class="h6 fw-semi-bold">$4393</span>-->                                
                            </div>
                            <b-progress class="bg-gray-lighter progress-sm mt-2" :value="getProviderGoalToScheduledPercent(rowKey, colKey)" :max="100" :variant="getOverallVariant(rowKey, colKey)"/>
                            <div class="clearfix fs-mini" v-if="getProviderGoalsTotalForDay(rowKey, colKey)">
                                <span class="pull-right m-0 fw-semi-bold mb-3">{{getProviderGoalsTotalForDay(rowKey, colKey) | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</span>
                                <span class="fs-mini" v-if="getDiffBetweenGoalAndScheduledForDay(rowKey, colKey) > 0">Needed to reach Goal: <span class="fw-semi-bold">{{getDiffBetweenGoalAndScheduledForDay(rowKey, colKey) | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</span></span>
                            </div>
                        </div>  
                    </Widget>
                </b-col>
                </b-row>
            </template>
        </b-container>

        <!--        
        <section>
            <Widget>
                <div class="button-panel">
                    <b-row>
                        <b-col md='4'>
                            <span class="fw-semi-bold">test1</span>
                        </b-col>
                        <b-col md='8'>
                            <span class="fw-semi-bold">test2</span>                    
                        </b-col>
                    </b-row>
                </div>
            </Widget>
        </section>
        -->
    </div>
</template>

<script>
/* eslint-disable */

import Vue from 'vue';
import Widget from '@/components/Widget/Widget';
import mixin from '../../../mixin.js';
import PopupErrorDisplay from "@/components/ErrorDisplay/PopupErrorDisplay";
import HuddleDetailsSkeleton from './HuddleDetailsSkeleton.vue';
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
import moment from 'moment-timezone';
import _ from 'lodash';

export default {
    name: 'HuddleDetails',
    components: {Widget, PopupErrorDisplay, HuddleDetailsSkeleton},
    props: ['d'],
    mixins: [mixin],

    created() {
        this.init();
    },

    data(){
        return {
            loadingStatus: false,
            results: [],
        }
    },

    methods: {

        ...mapActions('huddle', ['getHuddleDetailsData']),

        async init() {
            this.resetError();
            this.loadingStatus = true;
            let payload = {
                siteID: this.siteID,
                _date: this.d,
                country: localStorage.getItem('country') || 'USA',
                timezone: this.allSettings?.GENERAL_TIMEZONE?.PropertyValue || 'America/New_York'
            };
            try {
                this.results = await this.getHuddleDetailsData(payload);
            } catch (error) {
                this.$toast.error(error);
            } finally {
                this.loadingStatus = false;
            }
        },

        async closeSlideOutPanel() {            
          let returnResults = {
            test : false           
          } 
          this.$emit('closePanel',returnResults);
        },

        getDayName(d) {
            return moment(d).format('ddd');
        },

        getProviderGoal(provNum, dayKey) {
            if(this.providerGoals) {
                let goAhead = true;

                if(this.allSettings['HUDDLE_CHECK_PROVIDER_ON_SCHEDULE']) {
                    if(this.allSettings['HUDDLE_CHECK_PROVIDER_ON_SCHEDULE'].PropertyValue.toUpperCase() === 'TRUE') {
                        if(this.providerScheduledOn(provNum, dayKey))
                            goAhead = false;
                    }
                }

                if(goAhead) {
                    let dayNum = moment(dayKey).format('d');    // returns 1 for Monday
                    let p1 = this.providerGoals.find(p => p.ProvNum == provNum);
                    if(p1)
                        return p1.ProductionGoal[dayNum];
                    else
                        return 0;
                } else
                    return 0;
            } else
                return 0;
        },

        providerScheduledOn(provNum, dayKey) {
            if(this.dataToDisplay) {
                let provData = this.dataToDisplay[0].filter(p => p.ProvNum === parseInt(provNum) 
                    && p.SchedType === null && p.SchedDate === null
                    && p.AptDate === dayKey);
                if(provData.length > 0)
                    return true;
                else
                    return false;
            } else
                return false;
        },

        getProviderGoalPercent(scheduled, goal) {
            //return ((this.adminData[0].filter(t => t.completed === true).length /this.allSettings.OVERDUE_RECALL_DAILY_CALLS_GOAL.PropertyValue)*100).toFixed(0);
            return ((scheduled/goal)*100).toFixed(0);
        },

        getDiffBetweenGoalAndScheduled(scheduled, goal) {            
            let ret = goal - scheduled;
            return ret.toFixed(2);
        },

        getTotalScheduledForDay(weekKey, dayKey) {
            return _.sumBy(this.detailData[weekKey][dayKey], 'ScheduledProduction');
        },

        getDiffBetweenGoalAndScheduledForDay(weekKey, dayKey) {
            let totalScheduledForDay = this.getTotalScheduledForDay(weekKey, dayKey);
            let providerTotal = this.getProviderGoalsTotalForDay(weekKey, dayKey);
            if(providerTotal) {
                let ret = providerTotal - totalScheduledForDay;
                ret = ret.toFixed(2);
                return ret;
            }
            else
                return totalScheduledForDay;
        },

        getProviderGoalsTotalForDay(weekKey, dayKey) {
            //console.log(`weekKey: ${weekKey}, dayKey: ${dayKey}`);  
            // let's first find out what day it is
            let dayNum = moment(dayKey).format('d');    // returns 1 for Monday

            // only keep those providers that are available for the day
            let a = _.map(this.detailData[weekKey][dayKey], 'ProvNum');
            let b = _.map(a, _.toNumber);

            if(this.providerGoals) {
                let providers = this.providerGoals.filter(p => _.includes(b, p.ProvNum));
                let _this = this;
                let sum = _.sumBy(providers, function(p) {
                    if(p.ProductionGoal[dayNum] === "")
                        return 0;
                    else if(_this.allSettings && _this.allSettings['HUDDLE_CHECK_PROVIDER_ON_SCHEDULE']) {
                        if(_this.allSettings['HUDDLE_CHECK_PROVIDER_ON_SCHEDULE'].PropertyValue.toUpperCase() === 'TRUE') {
                            if(_this.providerScheduledOn(p.ProvNum, dayKey))
                                return 0;
                            else
                                return p.ProductionGoal[dayNum];
                        } else
                            return p.ProductionGoal[dayNum];
                    }
                    else
                        return p.ProductionGoal[dayNum];
                });

                if(sum > 0)
                    return sum;
                else
                    return undefined;
            } else
                return undefined;
        },

        getProviderGoalToScheduledPercent(weekKey, dayKey) {
            let totalScheduledForDay = this.getTotalScheduledForDay(weekKey, dayKey);
            let providerTotal = this.getProviderGoalsTotalForDay(weekKey, dayKey);
            if(providerTotal)
                return ((totalScheduledForDay/providerTotal)*100).toFixed(0);
            else
                return 0;
        },

        scheduledGoalMet(weekKey, dayKey) {
            return this.getDiffBetweenGoalAndScheduledForDay(weekKey, dayKey) <= 0;
        },

        getVariant(scheduled, goal) {
            const percent = this.getProviderGoalPercent(scheduled, goal);
            let variant;
            switch(true) {
                case (percent < 50): variant = 'danger'; break;
                case (percent < 90): variant = 'warning'; break;
                case (percent > 90): variant = 'success'; break;
                default: variant = 'warning'; break;
            }
            return variant;
        },

        getOverallVariant(scheduled, goal) {
            const percent = this.getProviderGoalToScheduledPercent(scheduled, goal);
            let variant;
            switch(true) {
                case (percent < 50): variant = 'danger'; break;
                case (percent < 90): variant = 'warning'; break;
                case (percent > 90): variant = 'success'; break;
                default: variant = 'warning'; break;
            }
            return variant;
        },

        // ===== Weekly rollup (Monday-Saturday, month-clamped) =====

        // The displayed week's span: Monday of the isoWeek through Saturday, clamped to the Monday's
        // month (a week that spills into the next month stops at that month's last day).
        weekRange(weekKey) {
            let days = Object.keys((this.detailData && this.detailData[weekKey]) || {});
            if(!days.length)
                return null;
            let mon = moment(days.slice().sort()[0]).startOf('isoWeek');   // Monday
            let sat = mon.clone().add(5, 'days');                          // Saturday
            let monthEnd = mon.clone().endOf('month');
            return { start: mon, end: sat.isAfter(monthEnd, 'day') ? monthEnd : sat };
        },

        // Whole-week Scheduled = the gated visible day columns (base, unchanged) PLUS the off-screen days
        // of the week (past / today / beyond the forward window), counting every completed-or-scheduled
        // procedure for real providers with the same fee-adjusted value.
        getWeeklyScheduled(weekKey) {
            let base = Object.keys((this.detailData && this.detailData[weekKey]) || {})
                .reduce((sum, dayKey) => sum + (this.getTotalScheduledForDay(weekKey, dayKey) || 0), 0);
            let r = this.weekRange(weekKey);
            if(!r)
                return base;
            let startStr = r.start.format('YYYY-MM-DD');
            let endStr = r.end.format('YYYY-MM-DD');
            let windowStart = this.windowStart;
            let windowEnd = this.windowEnd;
            let activeProvNums = this.activeProvNums;
            let extra = 0;
            for(const o of ((this.dataToDisplay && this.dataToDisplay[0]) || [])) {
                if(o.AptDate < startStr || o.AptDate > endStr)
                    continue;                                       // outside this week
                if(o.AptDate >= windowStart && o.AptDate <= windowEnd)
                    continue;                                       // already counted in base (visible)
                if((o.AptStatus === 1 || o.AptStatus === 2) && activeProvNums.has(String(o.ProvNum)))
                    extra += (o.AdjustedValue || 0);
            }
            return base + extra;
        },

        getWeeklyGoal(weekKey) {
            // Fixed weekly target over the full Mon-Sat (clamped) week, independent of what is booked,
            // so it stays consistent week to week. Blank/"" = 0; every provider with a goal for that
            // weekday counts; format('d') gives 1=Mon..6=Sat (Sat-open offices count Saturday).
            // Intentionally does NOT consult HUDDLE_CHECK_PROVIDER_ON_SCHEDULE -- this is a target.
            if(!this.providerGoals)
                return 0;
            let r = this.weekRange(weekKey);
            if(!r)
                return 0;
            let total = 0;
            let cur = r.start.clone();
            while(cur.isSameOrBefore(r.end, 'day')) {
                let dayNum = cur.format('d');   // 0=Sun .. 6=Sat
                for(const p of this.providerGoals) {
                    let g = p.ProductionGoal ? p.ProductionGoal[dayNum] : null;
                    if(g !== undefined && g !== null && g !== "")
                        total += Number(g) || 0;
                }
                cur.add(1, 'day');
            }
            return total;
        },

        getWeeklyGoalPercent(weekKey) {
            let goal = this.getWeeklyGoal(weekKey);
            if(!goal)
                return 0;
            return ((this.getWeeklyScheduled(weekKey) / goal) * 100).toFixed(0);
        },

        getWeeklyDiff(weekKey) {
            // positive => still needed to reach goal; negative => above goal
            return this.getWeeklyGoal(weekKey) - this.getWeeklyScheduled(weekKey);
        },

        weeklyGoalMet(weekKey) {
            let goal = this.getWeeklyGoal(weekKey);
            return goal > 0 && this.getWeeklyScheduled(weekKey) >= goal;
        },

        getWeeklyVariant(weekKey) {
            // No goal set for the week -> neutral (green) accent, not a misleading "failing" red.
            if(!this.getWeeklyGoal(weekKey))
                return 'success';
            const percent = Number(this.getWeeklyGoalPercent(weekKey));
            switch(true) {
                case (percent < 50): return 'danger';
                case (percent < 90): return 'warning';
                default: return 'success';
            }
        },

        getWeekRangeLabel(weekKey) {
            let r = this.weekRange(weekKey);
            if(!r)
                return '';
            let s = r.start, e = r.end;
            if(s.month() === e.month())
                return `${s.format('MMM D')} – ${e.format('D')}`;
            return `${s.format('MMM D')} – ${e.format('MMM D')}`;
        }
    },

    computed: {
        dataToDisplay() {
            if(this.results && this.results.data && this.results.data.length > 0)
                return this.results.data;
        },        

        detailData() {
            if(this.dataToDisplay && this.dataToDisplay[0]) {
                let final = {};
                let scheduledProvNums = this.scheduledProvNums;
                let activeProvNums = this.activeProvNums;
                let windowStart = this.windowStart;
                let windowEnd = this.windowEnd;

                // Group appointments into Monday-based (isoWeek) weeks.
                let c = _.groupBy(this.dataToDisplay[0], function (val) {
                    return moment(val.AptDate).isoWeek();
                });

                _.each(c, (val, key) => {
                    /*
                    Day columns show ONLY the forward 2-week window and still-scheduled (AptStatus=1)
                    appointments. The query now also returns completed appts and earlier/later days, but
                    those feed only the weekly header totals -- exclude them here. The schedule filter
                    (Lau's fix) then applies: a provider whose OD schedule is set up is shown only on days
                    they are actually scheduled; a provider with no schedule blocks at all (e.g. a
                    newly-added doctor) is shown if they are a real/active provider.
                    */
                    let filtered_array = _.filter(
                        val, function (o) {
                            if (o.AptStatus !== 1) return false;
                            if (o.AptDate < windowStart || o.AptDate > windowEnd) return false;
                            if (scheduledProvNums.has(String(o.ProvNum))) {
                                return o.SchedDate != null && o.AptDate === o.SchedDate;
                            }
                            return activeProvNums.has(String(o.ProvNum));
                        }
                    );

                    if (!filtered_array.length)
                        return;   // this week has no visible day columns -> don't render it

                    final[key] = {};
                    let temp = _.groupBy(filtered_array, "AptDate");
                    _.each(temp, (val2, key2) => {
                        final[key][key2] = _(val2)
                                .groupBy("ProvNum")
                                .map((n, provider) => ({
                                    ProvNum: provider,
                                    Abbr: n[0].Abbr,
                                    ScheduledProduction: _.sumBy(n, "AdjustedValue")
                                }))
                                .value();
                    });
                });
                return final;
            }
        },

        providerGoals() {
            if(this.dataToDisplay && this.dataToDisplay[1] && this.dataToDisplay[1].length > 0) {
                let goals = JSON.parse(this.dataToDisplay[1][0].PropertyValue);
                let providerGoals = goals.providerGoals ? goals.providerGoals : goals;
                return providerGoals;
            }
        },

        // Forward day-column window: tomorrow (_date+1) through _date+15 -- the range the panel has
        // always shown as day columns. (The query now returns a wider range for the weekly totals.)
        windowStart() {
            return moment(this.d).add(1, 'day').format('YYYY-MM-DD');
        },

        windowEnd() {
            return moment(this.d).add(15, 'day').format('YYYY-MM-DD');
        },

        // Providers whose OD schedule is set up (>=1 SchedType=1 block) among the FUTURE scheduled rows.
        // Used to gate the day columns (Lau's "hygiene booked but doctor not in office" fix).
        scheduledProvNums() {
            let rows = (this.dataToDisplay && this.dataToDisplay[0]) ? this.dataToDisplay[0] : [];
            let windowStart = this.windowStart;
            return new Set(
                rows.filter(o => o.AptStatus === 1 && o.AptDate >= windowStart && o.SchedDate != null)
                    .map(o => String(o.ProvNum))
            );
        },

        // Real, active providers. this.providers is loaded from a query already filtered to
        // IsHidden=0 AND IsNotPerson=0, so this excludes entity/org "providers" (e.g. "Batavia Family
        // Dental PLCC", IsNotPerson=1) from the un-scheduled fallback and the weekly totals.
        activeProvNums() {
            return new Set((this.providers || []).map(p => String(p.ProvNum)));
        },

        // Office-level Monthly Net Production goal (from the same HUDDLE_GOALS blob as providerGoals).
        monthlyGoal() {
            if(this.dataToDisplay && this.dataToDisplay[1] && this.dataToDisplay[1].length > 0) {
                try {
                    let goals = JSON.parse(this.dataToDisplay[1][0].PropertyValue);
                    return Number(goals.monthlyNetProduction) || 0;
                } catch(e) {
                    return 0;
                }
            }
            return 0;
        },

        // Simple reference pace: monthly goal spread over the average number of weeks in a month.
        suggestedWeeklyPace() {
            return this.monthlyGoal ? (this.monthlyGoal / 4.33) : 0;
        },

        siteTimezone() {
            return this.allSettings?.GENERAL_TIMEZONE?.PropertyValue || 'America/New_York';
        }
    }
}

</script>

<style src="./HuddleComponentsModern.scss" lang="scss" />

<style lang="scss" scoped>
/* Weekly rollup banner + monthly reference for the "Next 2 Weeks" panel.
   Scoped so it carries this component's data-v hash and can't leak to other pages. */
$hws-green:  #3abf94;
$hws-yellow: #f0ad4e;
$hws-red:    #e2607a;

.huddle-monthly-ref {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 6px;
    padding: 8px 14px;
    background: #f1f5f9;
    border-radius: 8px;
    font-size: 0.85rem;
    color: #475569;

    .hmr-value { font-weight: 700; color: #0f172a; }
    .hmr-sep   { color: #94a3b8; }
    .hmr-pace  { color: #64748b; }
}

.week-summary-row {
    margin-top: 4px;
}

.huddle-week-summary {
    padding: 12px 16px;
    margin-bottom: 14px;
    border-radius: 10px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-left: 4px solid $hws-green;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);

    &.is-warning { border-left-color: $hws-yellow; }
    &.is-danger  { border-left-color: $hws-red; }

    .hws-head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        justify-content: space-between;
        gap: 8px;
    }

    .hws-week {
        font-size: 1rem;
        font-weight: 700;
        color: #0f172a;
        text-transform: uppercase;
        letter-spacing: 0.02em;
    }

    .hws-figs {
        font-size: 0.9rem;
        color: #475569;

        strong { color: #0f172a; }

        .hws-slash { color: #cbd5e1; margin: 0 2px; }

        .hws-pct {
            margin-left: 8px;
            font-weight: 700;
            color: #334155;
        }

        .hws-thumb {
            margin-left: 6px;
            color: $hws-green;
            font-size: 1.05rem;
        }
    }

    .hws-bar {
        height: 10px;
        border-radius: 6px;
        ::v-deep .progress-bar { border-radius: 6px; }
    }

    .hws-foot {
        margin-top: 6px;
        font-size: 0.8rem;
        color: #64748b;

        strong { color: #0f172a; }
    }
}
</style>